


















































import { Component, Vue, Ref } from "vue-property-decorator";
import store from "@/store";
import moment from "moment";
import { apiGet } from "@/services/api";
//  @types
import { ExtendedVessel } from "@/types/vessel";
import { CdsMinMaxDates } from "@/types/cds/minMaxDates";
import { LoggingHistoryGraphQueryData } from "@/components/LoggingHistoryGraph.vue";
import { DataHistoryFilter, LoggingHistoryConfig } from "@/types/loggingHistoryConfig";
//  @components
import FeatureNotAvailable from "@/components/FeatureNotAvailable.vue";
import DataHistoryChart from "@/components/DataHistory/DataHistoryChart.vue";
import DataHistoryFilters from "@/components/DataHistory/DataHistoryFilters.vue";
import DataHistoryFiltersPanel from "@/components/DataHistory/DataHistoryFiltersPanel.vue";
import EmptyState from "@/components/DataHistory/EmptyState.vue";
//  @modules
import { getModule } from "vuex-module-decorators";
import VesselsModule from "@/store/clients/Vessels.module";
import CdsModule from "@/store/clients/Cds.module";

const Vessels = getModule(VesselsModule, store);
const Cds = getModule(CdsModule, store);

@Component({
  components: {
    FeatureNotAvailable,
    // DataHistoryChart: LoggingHistoryGraph,
    DataHistoryChart,
    DataHistoryFilters,
    DataHistoryFiltersPanel,
    EmptyState,
  },
})
export default class DataHistory extends Vue {
  @Ref("DataHistoryFiltersPanel") private DataHistoryFiltersPanel!: any;
  //  @Data
  queryData: LoggingHistoryGraphQueryData[] = [];
  lastSubmission: { config: LoggingHistoryConfig; filter: DataHistoryFilter } | null = null;

  //  @Getters
  get vessel(): ExtendedVessel | null {
    if (!Vessels.currentVessel) return null;
    return Vessels.currentVessel;
  }

  get featureEnabled(): boolean {
    if (!this.vessel) return false;

    return this.vessel.features.some(feature => feature.name === "LoggingHistory");
  }

  get themeClass(): string {
    return this.$vuetify.theme.dark ? "theme--dark" : "theme--light";
  }

  //  @Methods
  async onFiltersSubmit(configData: { config: LoggingHistoryConfig; filter: DataHistoryFilter }): Promise<void> {
    if (!this.vessel) return;

    this.lastSubmission = configData;

    const logVariableDistinctSplit = configData.filter.selectedLogVariableIds?.split(/\|/) ?? [""];
    const cdsFeatureDistinctSplit = configData.config.cdsFeatureIds?.split(/\|/) ?? [""];

    const logVariableIdsDistinct = logVariableDistinctSplit[0]?.split(/;/) ?? [];
    const cdsFeatureIdsDistinct = cdsFeatureDistinctSplit[0]?.split(/;/) ?? [];

    const logVariableIdsGroups = logVariableDistinctSplit.length > 1 ? logVariableDistinctSplit[1].split(/;/) : [];
    const cdsFeatureIdsGroups = cdsFeatureDistinctSplit.length > 1 ? cdsFeatureDistinctSplit[1].split(/;/) : [];

    let fromDate: string;
    let toDate: string;

    if (configData.config.timeConfigurationType === "AllValues") {
      if (this.vessel.onboardSystem === "Nautilus") {
        const cdsMinMaxDates = await apiGet<CdsMinMaxDates>(`/Cds/Data/MinMax?vesselId=${this.vessel.id}`);
        fromDate = cdsMinMaxDates.minDate;
        toDate = cdsMinMaxDates.maxDate;
      } else {
        fromDate = this.vessel.logDataMinDate;
        toDate = this.vessel.logDataMaxDate;
      }
    } else if (configData.config.timeConfigurationType === "LastVoyage") {
      fromDate = configData.config.lastVoyage!.startDate;
      toDate = configData.config.lastVoyage!.endDate;
    } else if (configData.config.timeConfigurationType === "LastPeriod") {
      const fromDateDate = new Date();
      fromDateDate.setUTCDate(fromDateDate.getUTCDate() - configData.config.lastNumberDays);
      fromDateDate.setUTCHours(fromDateDate.getUTCHours() - configData.config.lastNumberHours);
      fromDateDate.setUTCMinutes(fromDateDate.getUTCMinutes() - configData.config.lastNumberMinutes);
      fromDate = fromDateDate.toISOString();
      toDate = new Date().toISOString();
    } else {
      fromDate = configData.config.fromDateTime;
      toDate = configData.config.toDateTime;
    }

    const selectedTimeInMinutes = this.minutesDiff(new Date(fromDate), new Date(toDate));
    const granularity = {
      Auto: this.autoGranularity(selectedTimeInMinutes),
      Raw: "Raw",
      Minute: "Minute",
      Quarter: "QuarterHour",
      Hour: "Hour",
      Day: "Day",
    }[configData.config.dataInterval];

    const filterMinMax = configData.config.filterMinMax;
    const filterOutliers = configData.config.filterOutliers;
    const cdsGranularity = this.getCdsGranularity(granularity);

    // Distinct queries
    let queryData: LoggingHistoryGraphQueryData[] = logVariableIdsDistinct.map(logVariableIds => ({
      logDataFilter:
        logVariableIds === ""
          ? undefined
          : {
              logVariableIds,
              fromDate,
              toDate,
              granularity,
              filterMinMax,
              filterOutliers,
            },
      cdsDataQuery: undefined,
    }));

    const cdsQueryData: LoggingHistoryGraphQueryData[] = cdsFeatureIdsDistinct.map(cdsFeatureIds => ({
      logDataFilter: undefined,
      cdsDataQuery:
        cdsFeatureIds === ""
          ? undefined
          : {
              vesselId: this.vessel!.id,
              cdsFeatureIds: cdsFeatureIds,
              fromDate: fromDate,
              toDate: toDate,
              granularity: cdsGranularity,
              filterOutliers: filterOutliers,
            },
    }));
    queryData.push(...cdsQueryData);

    // Group queries (use max length to cover all groups)
    const maxGroups = Math.max(logVariableIdsGroups.length, cdsFeatureIdsGroups.length);
    const groupsQueryData: LoggingHistoryGraphQueryData[] = Array.from({ length: maxGroups }).map((_, i) => ({
      logDataFilter:
        logVariableIdsGroups[i] === undefined || logVariableIdsGroups[i] === ""
          ? undefined
          : {
              logVariableIds: logVariableIdsGroups[i],
              fromDate,
              toDate,
              granularity,
              filterMinMax,
              filterOutliers,
            },
      cdsDataQuery:
        cdsFeatureIdsGroups[i] === undefined || cdsFeatureIdsGroups[i] === ""
          ? undefined
          : {
              vesselId: this.vessel!.id,
              cdsFeatureIds: cdsFeatureIdsGroups[i],
              fromDate: fromDate,
              toDate: toDate,
              granularity: cdsGranularity,
              filterOutliers: filterOutliers,
            },
    }));

    queryData.push(...groupsQueryData);
    queryData = queryData.filter(q => q.logDataFilter != undefined || q.cdsDataQuery != undefined);

    this.queryData = queryData;
  }

  //Auto will set granularity to the most detailed option available.
  //Using same limits that are already defined in graph-config-data-interval.
  autoGranularity(selectedTimeInMinutes: number): string {
    if (selectedTimeInMinutes <= 1440) {
      return "Raw";
    } else if (selectedTimeInMinutes <= 2880) {
      return "Minute";
    } else if (selectedTimeInMinutes <= 20160) {
      return "QuarterHour";
    } else if (selectedTimeInMinutes <= 129600) {
      return "Hour";
    }

    return "Day";
  }

  minutesDiff(fromDate: Date, toDate: Date): number {
    const diff = (fromDate.getTime() - toDate.getTime()) / 1000 / 60;

    return Math.abs(Math.round(diff));
  }

  getCdsGranularity(granularity: string): string {
    // CDS only has hour and 15s data, but we also add day here, where we aggregate hour data before display.
    switch (granularity) {
      case "Raw":
        return "15s";
      case "Day":
        return "day";
      default:
        return "hour";
    }
  }

  onUpdateTimePeriod({ fromDateTime, toDateTime }: { fromDateTime: number; toDateTime: number }): void {
    // Update your filter with new time period and date range type
    const fromDateFormatted = moment.utc(fromDateTime).format("YYYY-MM-DDTHH:mm");
    const toDateFormatted = moment.utc(toDateTime).format("YYYY-MM-DDTHH:mm");
    this.DataHistoryFiltersPanel.Filter.dateRange = [fromDateFormatted, toDateFormatted];
    this.DataHistoryFiltersPanel.Filter.dateRangeType = "SetPeriod";
    // Submit the filter to update the config
    this.DataHistoryFiltersPanel.submit();
  }

  // @Hooks
  async created(): Promise<void> {
    await Cds.fetchFeatures();
  }

  get filterSummary(): string {
    if (!this.lastSubmission) return "";
    const { config } = this.lastSubmission;
    const type = config.timeConfigurationType;
    if (type === "AllValues") return "All available data shown";
    if (type === "LastVoyage" && config.lastVoyage) {
      const start = moment.utc(config.lastVoyage.startDate).format("D MMM YYYY");
      const end = moment.utc(config.lastVoyage.endDate).format("D MMM YYYY");
      const voyageLabel = (config.lastVoyage as any).voyageName || config.lastVoyage.id;
      return `Showing data for Voyage ${voyageLabel} (${start} - ${end})`;
    }
    if (type === "SetPeriod") {
      const start = config.fromDateTime ? moment.utc(config.fromDateTime).format("D MMM YYYY HH:mm") : "";
      const end = config.toDateTime ? moment.utc(config.toDateTime).format("D MMM YYYY HH:mm") : "";
      if (start && end) return `Date ${start} to ${end} shown`;
      if (start) return `Showing data from ${start} and onwards`;
      if (end) return `Showing data up to ${end}`;
      return "Custom date range shown";
    }
    if (type === "LastPeriod") {
      const parts: string[] = [];
      if (config.lastNumberDays) parts.push(`${config.lastNumberDays} day${config.lastNumberDays === 1 ? "" : "s"}`);
      if (config.lastNumberHours) parts.push(`${config.lastNumberHours} hour${config.lastNumberHours === 1 ? "" : "s"}`);
      if (config.lastNumberMinutes) parts.push(`${config.lastNumberMinutes} minute${config.lastNumberMinutes === 1 ? "" : "s"}`);
      return `Last ${parts.join(" ")} shown`;
    }
    return "";
  }
}
